// Ported verbatim (TypeScript erased) from Dolmenwood paint-shapes.ts, c3669f9.
// Original geometry, ratio buckets, seeded 16-shape pool and cache are preserved.
export const VIEW_H = 64;
export const DEFAULT_RATIO = 200 / 64;
export const RATIO_RANGE = { min: 0.6, max: 12 };
const RATIO_STEP = 0.5;
function clampRatio(ratio) {
  return Math.min(RATIO_RANGE.max, Math.max(RATIO_RANGE.min, ratio));
}
export function viewWidth(ratio) {
  return Math.round(clampRatio(ratio) * VIEW_H);
}
export function bucketRatio(ratio) {
  if (!Number.isFinite(ratio) || ratio <= 0)
    return DEFAULT_RATIO;
  const stepped = Math.round(clampRatio(ratio) / RATIO_STEP) * RATIO_STEP;
  return clampRatio(stepped);
}
const SPECS = {
  "brush-plate": {
    amp: 5.2,
    spacing: 17,
    smooth: true,
    taper: 0.34,
    bias: 1.7,
    inward: 0.5,
    corner: 0.35,
    splashes: 0,
    bow: 3.2,
    lead: 0.6
  },
  "key-plate": {
    amp: 1.9,
    spacing: 17,
    smooth: true,
    taper: 0.16,
    bias: 1.7,
    inward: 0.6,
    corner: 0.42,
    splashes: 0,
    bow: 1.1,
    lead: 0.5
  },
  "torn-plate": {
    amp: 3.9,
    spacing: 14,
    smooth: false,
    taper: 0,
    bias: 1,
    inward: 1,
    corner: 0,
    splashes: 0,
    bow: 0,
    lead: 0
  },
  "torn-note": {
    amp: 2.1,
    spacing: 11,
    smooth: false,
    taper: 0,
    bias: 1.35,
    inward: 0.85,
    corner: 0.06,
    splashes: 0,
    bow: 0,
    lead: 0
  },
  "torn-leaf": {
    amp: 0.62,
    spacing: 2.5,
    smooth: false,
    taper: 0,
    bias: 1.6,
    inward: 0.7,
    corner: 0.1,
    splashes: 0,
    bow: 0,
    lead: 0
  },
  "torn-sheet": {
    amp: 1.1,
    spacing: 2.5,
    smooth: false,
    taper: 0,
    bias: 1.35,
    inward: 0.8,
    corner: 0.12,
    splashes: 0,
    bow: 0,
    lead: 0
  },
  blotch: {
    amp: 10,
    spacing: 18,
    smooth: true,
    taper: 0.05,
    bias: 1.15,
    inward: 0.3,
    corner: 0.62,
    splashes: 3,
    bow: 0,
    lead: 0
  },
  pebble: {
    amp: 4,
    spacing: 13,
    smooth: true,
    taper: 0,
    bias: 1,
    inward: 0.6,
    corner: 0.9,
    splashes: 0,
    bow: 0,
    lead: 0
  },
  brushstroke: {
    amp: 2.9,
    spacing: 18,
    smooth: true,
    taper: 0.34,
    bias: 1.2,
    inward: 1,
    corner: 0.5,
    splashes: 0,
    bow: 0,
    lead: 0
  },
  wash: {
    amp: 2.6,
    spacing: 15,
    smooth: true,
    taper: 0,
    bias: 1.15,
    inward: 0.9,
    corner: 0.5,
    splashes: 0,
    bow: 0,
    lead: 0
  }
};
const SPLASH_REACH = { min: 0.5, max: 1.3 };
const SPLASH_SPREAD = 0.55;
export const OUTWARD_CAP = { soft: 10, hard: 15 };
function capOutward(reach) {
  const { soft, hard } = OUTWARD_CAP;
  if (reach <= soft)
    return reach;
  return soft + (hard - soft) * (1 - Math.exp(-(reach - soft) / (hard - soft)));
}
export const STROKE_PAD = 1.5;
export const BEZIER_SWING = 4;
export const MAX_OVERHANG = OUTWARD_CAP.hard + STROKE_PAD + BEZIER_SWING;
export function hashSeed(input) {
  let hash = 2166136261;
  for (let i = 0;i < input.length; i++) {
    hash ^= input.charCodeAt(i);
    hash = Math.imul(hash, 16777619);
  }
  return hash >>> 0;
}
function mulberry32(seed) {
  let state = seed >>> 0;
  return () => {
    state = state + 1831565813 >>> 0;
    let t = Math.imul(state ^ state >>> 15, 1 | state);
    t = t + Math.imul(t ^ t >>> 7, 61 | t) ^ t;
    return ((t ^ t >>> 14) >>> 0) / 4294967296;
  };
}
function round(value) {
  return Math.round(value * 10) / 10;
}
function walkBaseline(width, corner, spacing) {
  const radius = Math.min(width, VIEW_H) * 0.5 * corner;
  const samples = [];
  const straight = (x0, y0, x1, y1, nx, ny) => {
    const count = Math.max(1, Math.round(Math.hypot(x1 - x0, y1 - y0) / spacing));
    for (let i = 0;i < count; i++) {
      const t = i / count;
      samples.push({ x: x0 + (x1 - x0) * t, y: y0 + (y1 - y0) * t, nx, ny });
    }
  };
  const arc = (ccx, ccy, from) => {
    if (radius < 0.5)
      return;
    const count = Math.max(3, Math.round(radius * Math.PI / 2 / spacing));
    for (let i = 0;i < count; i++) {
      const angle = from + Math.PI / 2 * (i / count);
      samples.push({
        x: ccx + radius * Math.cos(angle),
        y: ccy + radius * Math.sin(angle),
        nx: Math.cos(angle),
        ny: Math.sin(angle)
      });
    }
  };
  straight(radius, 0, width - radius, 0, 0, -1);
  arc(width - radius, radius, -Math.PI / 2);
  straight(width, radius, width, VIEW_H - radius, 1, 0);
  arc(width - radius, VIEW_H - radius, 0);
  straight(width - radius, VIEW_H, radius, VIEW_H, 0, 1);
  arc(radius, VIEW_H - radius, Math.PI / 2);
  straight(0, VIEW_H - radius, 0, radius, -1, 0);
  arc(radius, radius, Math.PI);
  return samples;
}
function buildPoints(spec, seed, width) {
  const random = mulberry32(seed);
  const cx = width / 2;
  const cy = VIEW_H / 2;
  const wobble = () => {
    const signed = random() * 2 - 1;
    const reach = Math.abs(signed) ** spec.bias * spec.amp;
    return signed >= 0 ? reach : -reach * spec.inward;
  };
  const drift = () => (random() * 2 - 1) * spec.amp * 0.3;
  const bases = walkBaseline(width, spec.corner, spec.spacing);
  let points = bases.map(({ x, y, nx, ny }) => {
    const out = wobble();
    const along = drift();
    return [x + nx * out - ny * along, y + ny * out + nx * along];
  });
  for (let i = 0;i < spec.splashes; i++) {
    const index = Math.floor(random() * points.length);
    const reach = spec.amp * (SPLASH_REACH.min + random() * (SPLASH_REACH.max - SPLASH_REACH.min));
    const nx = (points[index][0] - cx) / cx;
    const ny = (points[index][1] - cy) / cy;
    const length = Math.hypot(nx, ny) || 1;
    for (const [offset, share] of [
      [0, 1],
      [-1, SPLASH_SPREAD],
      [1, SPLASH_SPREAD]
    ]) {
      const j = (index + offset + points.length) % points.length;
      points[j] = [
        points[j][0] + nx / length * reach * share,
        points[j][1] + ny / length * reach * share
      ];
    }
  }
  for (let i = 0;i < points.length; i++) {
    const { x, y, nx, ny } = bases[i];
    const out = (points[i][0] - x) * nx + (points[i][1] - y) * ny;
    const capped = capOutward(out);
    if (capped === out)
      continue;
    points[i] = [points[i][0] + nx * (capped - out), points[i][1] + ny * (capped - out)];
  }
  if (spec.taper > 0) {
    const profile = (t) => {
      const u = Math.min(1, Math.max(0, t));
      const symmetric = Math.sin(Math.PI * u) ** 0.5;
      const skew = 1 + spec.lead * (2 * u - 1);
      return 1 - spec.taper + spec.taper * Math.min(1, symmetric * skew);
    };
    points = points.map(([x, y]) => [x, cy + (y - cy) * profile(x / width)]);
  }
  if (spec.bow !== 0) {
    points = points.map(([x, y]) => [
      x,
      y - spec.bow * Math.sin(Math.PI * Math.min(1, Math.max(0, x / width)))
    ]);
  }
  return points;
}
function smoothSegments(points) {
  const n = points.length;
  return points.map((p1, i) => {
    const p0 = points[(i - 1 + n) % n];
    const p2 = points[(i + 1) % n];
    const p3 = points[(i + 2) % n];
    return [
      [p1[0] + (p2[0] - p0[0]) / 6, p1[1] + (p2[1] - p0[1]) / 6],
      [p2[0] - (p3[0] - p1[0]) / 6, p2[1] - (p3[1] - p1[1]) / 6],
      p2
    ];
  });
}
const geometryCache = new Map;
const maskCache = new Map;
export const SHAPE_POOL = 16;
function poolSeed(seed) {
  return (seed % SHAPE_POOL + SHAPE_POOL) % SHAPE_POOL;
}
export function roughGeometry(variant, seed, ratio = DEFAULT_RATIO) {
  const width = viewWidth(ratio);
  const form = poolSeed(seed);
  const key = `${variant}:${form}:${width}`;
  const cached = geometryCache.get(key);
  if (cached !== undefined)
    return cached;
  const spec = SPECS[variant];
  const points = buildPoints(spec, form, width);
  const segments = spec.smooth ? smoothSegments(points) : [];
  const path = spec.smooth ? `M${round(points[0][0])} ${round(points[0][1])}` + segments.map((s) => `C${s.map(([x, y]) => `${round(x)} ${round(y)}`).join(",")}`).join("") + "Z" : `${points.map(([x, y], i) => `${i === 0 ? "M" : "L"}${round(x)} ${round(y)}`).join("")}Z`;
  const hull = [...points, ...segments.flat()];
  const xs = hull.map(([x]) => x);
  const ys = hull.map(([, y]) => y);
  const minX = Math.min(...xs) - STROKE_PAD;
  const maxX = Math.max(...xs) + STROKE_PAD;
  const minY = Math.min(...ys) - STROKE_PAD;
  const maxY = Math.max(...ys) + STROKE_PAD;
  const pct = (gap, extent) => `${(-gap / extent * 100).toFixed(2)}%`;
  const geometry = {
    path,
    viewBox: `${round(minX)} ${round(minY)} ${round(maxX - minX)} ${round(maxY - minY)}`,
    inset: {
      top: pct(-minY, VIEW_H),
      bottom: pct(maxY - VIEW_H, VIEW_H),
      left: pct(-minX, width),
      right: pct(maxX - width, width)
    }
  };
  geometryCache.set(key, geometry);
  return geometry;
}
export function roughPath(variant, seed, ratio = DEFAULT_RATIO) {
  return roughGeometry(variant, seed, ratio).path;
}
export function roughMask(variant, seed, ratio = DEFAULT_RATIO) {
  const key = `${variant}:${poolSeed(seed)}:${viewWidth(ratio)}`;
  const cached = maskCache.get(key);
  if (cached !== undefined)
    return cached;
  const { path, viewBox } = roughGeometry(variant, seed, ratio);
  const svg = `<svg xmlns="http://www.w3.org/2000/svg" viewBox="${viewBox}" preserveAspectRatio="none">` + `<path d="${path}" fill="#fff"/></svg>`;
  const url = `url("data:image/svg+xml,${encodeURIComponent(svg)}")`;
  maskCache.set(key, url);
  return url;
}
